import React, { useMemo } from "react";
import { StepHelpers } from "@shared/components/Stepper";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { Zap, ChevronRight, Check, Eye, EyeOff } from "lucide-react";
import { toast } from "@shared/hooks/use-toast";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { cn } from "@shared/lib/utils";

interface UsPaymentConnectionStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

const PrefixBadge = ({ text }: { text: string }) => (
  <span className="inline-flex items-center justify-center bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded-sm border border-gray-200 shadow-sm mr-2 absolute left-2 top-1/2 -translate-y-1/2">
    {text}
  </span>
);

export const UsPaymentConnectionStep: React.FC<
  UsPaymentConnectionStepProps
> = ({ stepHelpers, currentStepIndex, onNext, onPrevious, isValidating }) => {
  const isVerified = "success";
  const platformUrl = import.meta.env.VITE_PLATFORM_URL;
  const urlParts = platformUrl?.split("//")[1]?.split(".") || [];
  const rawEnvironment = urlParts[0]?.replace("platform-", "") || "production";
  const formattedEnvironment =
    rawEnvironment.charAt(0).toUpperCase() + rawEnvironment.slice(1);
  const dynamicIntegrationName = `Us Payment — ${formattedEnvironment}`;

  const form = useForm({
    defaultValues: {
      integrationName: dynamicIntegrationName,
      accountLookupUrl: "",
      paymentNotificationUrl: "",
      password: "",
      username: "",
      enterpriseId: "",
      companyId: "",
    },
    mode: "onChange",
  });

  const renderUrlField = (
    field: FormField,
    formMethod: any,
    error?: string,
    prefixText: string = "GET",
    plClass: string = "pl-[60px]",
  ) => {
    return (
      <Controller
        name={field.name}
        control={formMethod.control}
        render={({ field: controllerField }) => {
          return (
            <div className="relative flex items-center w-full">
              <PrefixBadge text={prefixText} />
              <input
                {...controllerField}
                type="url"
                id={field.name}
                placeholder={field.placeholder}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  error ? "border-red-500" : "border-gray-300"
                } ${plClass}`}
              />
            </div>
          );
        }}
      />
    );
  };

  const fields: FormField[] = useMemo(
    () => [
      {
        name: "integrationName",
        label: "Integration name",
        type: "text",
        required: true,
        placeholder: `Enter a friendly name for connection`,
        helperText:
          "A friendly name shown across SMART360 to identify this connection.",
        showHelperTooltip: true,
        className: "col-span-2",
        defaultValue: dynamicIntegrationName,
      },
      {
        name: "accountLookupUrl",
        label: "Account Lookup URL",
        type: "text",
        required: true,
        placeholder: "https://yourdomain.com/account-lookup",
        customRender: (field, formMethod, error) =>
          renderUrlField(field, formMethod, error, "GET", "pl-[60px]"),
        helperText:
          "SMART360 calls this with ?accountId= to validate a customer account before payment.",
        className: "col-span-2",
      },
      {
        name: "paymentNotificationUrl",
        label: "Payment Notification URL",
        type: "custom",
        required: true,
        placeholder: "https://yourdomain.com/payment-notify",
        customRender: (field, formMethod, error) =>
          renderUrlField(field, formMethod, error, "POST", "pl-[68px]"),
        helperText:
          "SMART360 POSTs payment confirmation here after each completed transaction.",
        className: "col-span-2",
      },
      {
        name: "password",
        label: "Password",
        type: "password",
        required: true,
        disableAutofill: true,
        placeholder: "",
        helperText: "Sent as X-Password header. Authenticates the API caller.",
        className: "col-span-1",
      },
      {
        name: "username",
        label: "Username (optional)",
        type: "text",
        disableAutofill: true,
        placeholder: "Leave blank if not required",
        helperText: "Sent as X-Username header.",
        className: "col-span-1",
      },
      {
        name: "enterpriseId",
        label: "Enterprise ID (optional)",
        type: "text",
        placeholder: "e.g. ENT-0042",
        helperText:
          "Sent as X-EnterpriseId. Use when multiple enterprises share one endpoint.",
        className: "col-span-1",
      },
      {
        name: "companyId",
        label: "Company ID (optional)",
        type: "text",
        placeholder: "e.g. CMP-0012",
        helperText:
          "Sent as X-CompanyId. Use when multiple companies share one enterprise.",
        className: "col-span-1",
      },
    ],
    [],
  );

  const handleTestConnection = async () => {
    toast({
      title: "Testing Connection...",
      description: "Pinging endpoints with provided credentials.",
    });
    // Simulate test delay
    setTimeout(() => {
      toast({
        title: "Connection Successful",
        description: "Both endpoints returned HTTP 200 OK.",
      });
    }, 1500);
  };

  const renderCustomFooter = React.useCallback(
    () => (
      <div className="space-y-6 col-span-2">
        {/* Test connection block */}
        <div className="border-t border-gray-200 pt-6 mt-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <h3 className="text-base font-bold text-gray-900">
                Test connection
              </h3>
              <p className="text-sm text-gray-500 font-normal max-w-2xl">
                SMART360 will ping both endpoints with your credentials to
                verify reachability.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleTestConnection}
            >
              <Zap className="w-4 h-4 text-gray-700 mr-2" />
              Test Connection
            </Button>
          </div>

          {/* Verification successful banner */}
          {isVerified === "success" && (
            <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl p-4 flex gap-3 items-start animate-in fade-in slide-in-from-bottom-2 duration-200">
              <Check className="w-5 h-5 text-[#16a34a] shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="text-sm font-bold text-[#15803d] block">
                  Connection successful
                </span>
                <span className="text-[13px] text-gray-600 font-normal block leading-relaxed">
                  Endpoints responded in 218ms
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Duplicate payment handling block */}
        <div className="border-t border-gray-200 pt-6 mt-6 flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-base font-bold text-gray-900">
              Duplicate payment handling
            </h3>
            <p className="text-sm text-gray-500 font-normal max-w-2xl">
              When your system returns HTTP 409, surface it as a named Duplicate
              alert instead of a generic failure.
            </p>
          </div>
          <Switch defaultChecked />
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-end pt-6 border-t mt-8 gap-4">
          <span className="text-sm text-gray-500 font-medium">Step 1 of 2</span>
          <Button
            onClick={onNext}
            variant="default"
            className="bg-[#5850ec] hover:bg-[#4f46e5]"
          >
            <span>Continue</span>
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    ),
    [handleTestConnection, isVerified, onNext],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b pb-4">
        <PageHeader
          title="Credentials & Endpoints"
          subtitle={`Provide your endpoint URLs and authentication headers. SMART360 will call these for every account lookup and payment notification.`}
        />

        {/* Top Right Status Pill */}
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
            isVerified
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-gray-100 text-gray-600 border-gray-200"
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${isVerified ? "bg-emerald-500" : "bg-gray-400"}`}
          />
          {isVerified ? "Connection verified" : "Not yet tested"}
        </span>
      </div>

      <DynamicForm
        fields={fields}
        form={form}
        className="!gap-y-2" // Override vertical gap to be smaller
        onSubmit={handleTestConnection}
        onFieldChange={handleTestConnection}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          showProgress: false,
          classes: FIELD_CLASSES,
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
